import type { ArabicCatalog } from "@ncp/i18n";
import type en from "./reviewChecks.en";

/** ما يسجله الموظف لكي تختبر مراجعة المرحلة ما يطلبه كل معيار (التحقق من تقييم AA-01، المجموعة G3). */
const ar: ArabicCatalog<typeof en> = {
  replyPoints: {
    title: "الرد نقطة نقطة",
    hint: "تؤخذ كل نقطة يثيرها الطرف ويجاب عنها: بالقبول، أو بالاستبعاد مع الأسباب، أو بطلب الدليل. ويتحقق الفحص من الإجابة عن كل نقطة ومن وصول الإجابات إلى التقرير.",
    point: "النقطة التي أثارها الطرف",
    pointAr: "النقطة بالعربية (اختياري)",
    answer: "إجابة الإدارة",
    answers: { Accepted: "مقبولة", Discounted: "مستبعدة", EvidenceRequested: "طلب الدليل" },
    unanswered: "لم يجب عنها بعد",
    reasons: "الأسباب",
    reasonsAr: "الأسباب بالعربية (اختياري)",
    add: "إضافة نقطة",
    remove: "حذف",
    save: "حفظ النقاط",
    saved: "حفظت النقاط.",
    progress: "أجيب عن {{answered}} من {{total}} من النقاط",
    answeredBy: "أجيب في {{date}} من {{by}}",
    empty: "لم تسجل أي نقطة بعد. يقسم الرد إلى النقاط التي يثيرها.",
  },
  findings: {
    composed: "إجابة الإدارة كما جمعت من النقاط (تقتبس في البند 4.6 من التقرير)",
  },
  reportReplies: {
    title: "الردود على التقرير الأولي",
    hint: "تقرأ الردود الواردة خلال {{period.complaint.preliminaryReportReply}} يوم عمل، وتتناول كل نقطة منها للتقرير النهائي. وهي ليست طلبات معلومات.",
    party: "الطرف",
    partyOther: "طرف آخر (الاسم)",
    receivedAt: "تاريخ الورود",
    summary: "مضمون الرد",
    record: "تسجيل الرد",
    recorded: "سجل الرد.",
    none: "لم يسجل أي رد بعد.",
    considered: "نظر فيه في {{date}} من {{by}}",
    late: "بعد انتهاء المهلة",
  },
  classification: {
    title: "تصنيف الإدارة للسلوك",
    hint: "لا تتقيد الوزارة بتكييف الشاكي (المادة 23(4) من اللائحة التنفيذية): تسمى الممارسة المحظورة التي تنطبق عليها الوقائع والمادة وكيفية انطباق أركانها على الوقائع.",
    complainant: "يصنف الشاكي السلوك على أنه {{conduct}} ({{articles}})",
    conduct: "الممارسة المحظورة",
    articles: "المواد (مفصولة بفواصل، مثل 6، 8)",
    reasoning: "كيفية انطباق أركان المادة على الوقائع",
    reasoningAr: "الأسباب بالعربية (اختياري)",
    save: "تسجيل التصنيف",
    saved: "سجل التصنيف.",
    by: "صنف في {{date}} من {{by}}",
  },
  standing: {
    title: "صفة الشاكي والضرر",
    hint: "يثبت المنافس أو العميل أو المورد مصلحة مشروعة، ويثبت المستهلك ضررا بمصالحه (المادة 32؛ الدليل الإرشادي للشكاوى، القسم 2.3). ويكون الضرر محددا متى بين نوعه وقدر أو أرخ.",
    relationship: "الشاكي بالنسبة إلى المشكو في حقه…",
    relationships: { Competitor: "منافس", Customer: "عميل", Supplier: "مورد", Distributor: "موزع", Consumer: "مستهلك", GovernmentEntity: "جهة حكومية", Association: "جمعية", None: "لا صلة مبينة" },
    activeInMarket: "نشط في السوق المعنية",
    harmKind: "نوع الضرر",
    estimatedHarm: "الضرر المقدر",
    harmKinds: { HigherPrices: "ارتفاع الأسعار", LostSales: "خسارة المبيعات", RefusalToSupply: "رفض التوريد", ExclusionFromMarket: "الإقصاء من السوق", DiscriminatoryTerms: "شروط تمييزية", ReducedChoiceOrQuality: "تراجع الخيارات أو الجودة", Other: "ضرر آخر" },
    notStated: "غير مبين",
    save: "تسجيل الصفة والضرر",
    saved: "سجلت الصفة والضرر.",
  },
  contradiction: {
    status: { Open: "مفتوح", Resolved: "محلول", CarriedAsIssue: "منقول مسألة مفتوحة" },
    resolve: "حل",
    carry: "نقل مسألة مفتوحة",
    note: "كيفية الحل أو سبب النقل",
    save: "تسجيل",
    saved: "بت في التناقض.",
  },
  study: {
    conduct: "السلوك المشتبه به",
    market: "السوق المعنية",
    harm: "الضرر المحتمل",
    elementsHint: "يسجل كل من العناصر الثلاثة على حدة، وتتحقق المراجعة منها جميعا قبل جمع المعلومات.",
  },
  enforcement: {
    factsTitle: "انتقال السيطرة والحدود",
    controlEvidence: "ما يثبت انتقال السيطرة (بند مستندات العملية أو رد الأطراف)",
    thresholdTest: "حد المادة (12) المستوفى",
    thresholdTests: { Turnover: "المبيعات في السوق المعنية", Share: "الحصة السوقية", Both: "كلاهما" },
    registerTitle: "سجل التركزات الاقتصادية",
    registerHint: "يرجع إلى السجل للتحقق من وجود إخطار بهذه العملية قبل التوصية بالنتيجة.",
    registerNone: "لا يظهر السجل أي إخطار",
    registerFound: "يوجد إخطار في السجل",
    registerNote: "ملاحظة (اختياري)",
    registerRecord: "تسجيل الرجوع إلى السجل",
    registerRecorded: "سجل الرجوع إلى السجل.",
    registerChecked: "روجع في {{date}} من {{by}}: {{result}}",
  },
  remedies: {
    harm: "ضرر التقرير الذي يعالجه هذا التعهد",
    harms: { HorizontalNonCoordinated: "الآثار الأفقية غير المنسقة", HorizontalCoordinated: "الآثار الأفقية المنسقة", Vertical: "الغلق الرأسي", Conglomerate: "الآثار التكتلية", NonPrice: "الآثار غير السعرية" },
    harmReasoning: "سبب معالجته لذلك الضرر في سوقه",
    chooseHarm: "الضرر الذي يعالجه",
    structural: "سبب عدم تفضيل تدبير بنيوي",
    structuralHint: "مطلوب حين لا تتضمن الحزمة المقبولة أي تعهد بنيوي.",
    tieHint: "تربط الحزمة المقبولة كل تعهد، مع الأسباب، بضرر يحدده التقرير في سوق التعهد.",
  },
  decision: {
    validUntil: "سار حتى",
    validUntilHint: "يبين الاستثناء الممنوح مدة صلاحيته (المادة 10(4) من المرسوم بقانون).",
  },
};

export default ar;
